import { env } from '@/env'

type FrontendErrorType = 'window-error' | 'unhandled-rejection' | 'react-error-boundary'

type FrontendErrorReport = {
  type: FrontendErrorType
  message: string
  stack?: string | null
  componentStack?: string | null
  route: string
  url: string
  userAgent: string
  release?: string
  timestamp: string
  metadata?: Record<string, unknown>
}

const reportedFingerprints = new Set<string>()

function buildFingerprint(report: FrontendErrorReport) {
  return [
    report.type,
    report.message,
    report.stack ?? '',
    report.componentStack ?? '',
    report.route,
  ].join('::')
}

function sendReport(report: FrontendErrorReport) {
  const payload = JSON.stringify(report)

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([payload], { type: 'application/json' })
    navigator.sendBeacon('/api/client-errors', blob)
    return
  }

  void fetch('/api/client-errors', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: payload,
    credentials: 'include',
    keepalive: true,
  })
}

export function reportFrontendError(
  type: FrontendErrorType,
  input: {
    message: string
    stack?: string | null
    componentStack?: string | null
    metadata?: Record<string, unknown>
  },
) {
  const report: FrontendErrorReport = {
    type,
    message: input.message,
    stack: input.stack ?? null,
    componentStack: input.componentStack ?? null,
    route: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    url: window.location.href,
    userAgent: window.navigator.userAgent,
    release: env.VITE_APP_RELEASE,
    timestamp: new Date().toISOString(),
    metadata: input.metadata,
  }

  const fingerprint = buildFingerprint(report)
  if (reportedFingerprints.has(fingerprint)) {
    return
  }

  reportedFingerprints.add(fingerprint)
  sendReport(report)
}

export function formatUnknownError(error: unknown) {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
    }
  }

  if (typeof error === 'string') {
    return {
      message: error,
      stack: null,
    }
  }

  return {
    message: 'Unknown frontend error',
    stack: null,
  }
}
